// client/src/pages/Admin/ProjectManager/constants/project.constants.ts
/**
 * PROJECT MANAGER CONSTANTS
 * 
 * This file contains all configuration constants, options, and static data
 * used throughout the ProjectManager feature.
 * 
 * @module ProjectManager/Constants
 */

import {
    ProjectStatus,
    ProjectCategory,
    ProjectImpact
} from '../../../../shared/models/Project';
import type { LucideIcon } from 'lucide-react';
import {
    Archive,
    BookOpen,
    BriefcaseBusiness,
    Building2,
    CheckCircle2,
    Clock3,
    Droplets,
    FileText,
    Flame,
    GraduationCap,
    Handshake,
    HeartPulse,
    Landmark,
    Leaf,
    Pin,
    Sparkles,
    Sprout,
    TrendingUp,
    TriangleAlert,
} from 'lucide-react';

// ============================================
// PAGINATION CONSTANTS
// ============================================

/**
 * Default number of items per page
 */
export const DEFAULT_PAGE_SIZE = 10;

/**
 * Page size options for the pagination dropdown
 */
export const PAGE_SIZE_OPTIONS = [
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 100, label: '100' }
] as const;

/**
 * Maximum number of page numbers to show in pagination
 */
export const MAX_VISIBLE_PAGES = 5;

// ============================================
// SEARCH CONSTANTS
// ============================================

/**
 * Debounce delay for search input (in milliseconds)
 */
export const DEBOUNCE_DELAY = 300;

/**
 * Minimum characters before showing search suggestions
 */
export const MIN_SEARCH_CHARS = 2;

/**
 * Maximum number of search suggestions to display
 */
export const MAX_SEARCH_SUGGESTIONS = 8;

// ============================================
// SORT CONSTANTS
// ============================================

/**
 * Sort options for the project list
 */
export const SORT_OPTIONS = [
    { value: 'newest', label: 'Newest First', defaultDirection: 'desc' },
    { value: 'oldest', label: 'Oldest First', defaultDirection: 'asc' },
    { value: 'alphabetical', label: 'Alphabetical (A-Z)', defaultDirection: 'asc' },
    { value: 'alphabetical-desc', label: 'Alphabetical (Z-A)', defaultDirection: 'desc' },
    { value: 'recently-updated', label: 'Recently Updated', defaultDirection: 'desc' },
    { value: 'views', label: 'Most Viewed', defaultDirection: 'desc' },
    { value: 'start-date', label: 'Start Date', defaultDirection: 'desc' },
    { value: 'impact', label: 'Impact Level', defaultDirection: 'desc' }
] as const;

/**
 * Sort direction options
 */
export const SORT_DIRECTIONS = {
    ASC: 'asc' as const,
    DESC: 'desc' as const
} as const;

// ============================================
// FILTER CONSTANTS
// ============================================

/**
 * All filter options for the project list
 */
export const FILTER_OPTIONS = {
    status: [
        { value: 'all', label: 'All Statuses' },
        { value: ProjectStatus.DRAFT, label: 'Draft' },
        { value: ProjectStatus.PUBLISHED, label: 'Published' },
        { value: ProjectStatus.COMING_SOON, label: 'Coming Soon' },
        { value: ProjectStatus.ARCHIVED, label: 'Archived' }
    ],
    category: [
        { value: 'all', label: 'All Categories' },
        { value: ProjectCategory.GOVERNANCE, label: 'Governance' },
        { value: ProjectCategory.POLICY_ADVOCACY, label: 'Policy Advocacy & Reform' },
        { value: ProjectCategory.ENVIRONMENTAL, label: 'Environmental Sustainability' },
        { value: ProjectCategory.RESEARCH_DATA, label: 'Research & Data Collection' },
        { value: ProjectCategory.PARTNERSHIPS, label: 'Partnership Building' },
        { value: ProjectCategory.CULTURAL_SHIFT, label: 'Cultural Paradigm Shift' },
        { value: ProjectCategory.REMEMBRANCE, label: 'Doctrine of Remembrance' }
    ],
    impact: [
        { value: 'all', label: 'All Impact Levels' },
        { value: ProjectImpact.LOW, label: 'Low Impact' },
        { value: ProjectImpact.MEDIUM, label: 'Medium Impact' },
        { value: ProjectImpact.HIGH, label: 'High Impact' },
        { value: ProjectImpact.CRITICAL, label: 'Critical Impact' }
    ],
    featured: [
        { value: 'all', label: 'All Projects' },
        { value: 'true', label: 'Featured Only' },
        { value: 'false', label: 'Non-Featured Only' }
    ],
    ongoing: [
        { value: 'all', label: 'All Projects' },
        { value: 'true', label: 'Ongoing Only' },
        { value: 'false', label: 'Completed Only' }
    ]
} as const;

// ============================================
// STATUS CONSTANTS
// ============================================

/**
 * All valid project statuses
 */
export const STATUS_OPTIONS: ProjectStatus[] = [
    ProjectStatus.DRAFT,
    ProjectStatus.PUBLISHED,
    ProjectStatus.COMING_SOON,
    ProjectStatus.ARCHIVED
];

/**
 * Status configuration with colors and icons
 */
export const STATUS_CONFIG: Record<ProjectStatus, {
    label: string;
    color: string;
    bgColor: string;
    icon: LucideIcon;
    description: string;
}> = {
    [ProjectStatus.DRAFT]: {
        label: 'Draft',
        color: '#b45309',
        bgColor: '#fef3c7',
        icon: FileText,
        description: 'Project is in draft mode and not visible to the public'
    },
    [ProjectStatus.PUBLISHED]: {
        label: 'Published',
        color: '#15803d',
        bgColor: '#dcfce7',
        icon: CheckCircle2,
        description: 'Project is published and visible to the public'
    },
    [ProjectStatus.COMING_SOON]: {
        label: 'Coming Soon',
        color: '#9a3412',
        bgColor: '#fed7aa',
        icon: Clock3,
        description: 'Project is coming soon and not yet visible to the public'
    },
    [ProjectStatus.ARCHIVED]: {
        label: 'Archived',
        color: '#4b5563',
        bgColor: '#f0f2f5',
        icon: Archive,
        description: 'Project is archived and not visible to the public'
    }
};

// ============================================
// IMPACT CONSTANTS
// ============================================

/**
 * Impact configuration with colors and descriptions
 */
export const IMPACT_CONFIG: Record<ProjectImpact, {
    label: string;
    color: string;
    bgColor: string;
    icon: LucideIcon;
    description: string;
}> = {
    [ProjectImpact.LOW]: {
        label: 'Low Impact',
        color: '#8892a0',
        bgColor: '#f0f2f5',
        icon: TrendingUp,
        description: 'Project has limited impact on the community'
    },
    [ProjectImpact.MEDIUM]: {
        label: 'Medium Impact',
        color: '#f5a623',
        bgColor: '#fff8e1',
        icon: TrendingUp,
        description: 'Project has moderate impact on the community'
    },
    [ProjectImpact.HIGH]: {
        label: 'High Impact',
        color: '#ed1c24',
        bgColor: '#fce8e8',
        icon: Flame,
        description: 'Project has significant impact on the community'
    },
    [ProjectImpact.CRITICAL]: {
        label: 'Critical Impact',
        color: '#e63946',
        bgColor: '#ffebee',
        icon: TriangleAlert,
        description: 'Project has critical impact on the community'
    }
};

// ============================================
// CATEGORY CONSTANTS
// ============================================

/**
 * Category configuration with descriptions and icons
 */
export const CATEGORY_CONFIG: Record<ProjectCategory, {
    label: string;
    icon: LucideIcon;
    color: string;
    description: string;
}> = {
    [ProjectCategory.GOVERNANCE]: {
        label: 'Governance',
        icon: Landmark,
        color: '#8b5cf6',
        description: 'Good governance and accountability programs'
    },
    [ProjectCategory.POLICY_ADVOCACY]: {
        label: 'Policy Advocacy & Reform',
        icon: BriefcaseBusiness,
        color: '#f59e0b',
        description: 'Policy and reform programs'
    },
    [ProjectCategory.ENVIRONMENTAL]: {
        label: 'Environmental Sustainability',
        icon: Leaf,
        color: '#22c55e',
        description: 'Environmental conservation and protection programs'
    },
    [ProjectCategory.RESEARCH_DATA]: {
        label: 'Research & Data Collection',
        icon: BookOpen,
        color: '#3b82f6',
        description: 'Research and data insights programs'
    },
    [ProjectCategory.PARTNERSHIPS]: {
        label: 'Partnership Building',
        icon: Handshake,
        color: '#14b8a6',
        description: 'Partnership programs and collaboration'
    },
    [ProjectCategory.CULTURAL_SHIFT]: {
        label: 'Cultural Paradigm Shift',
        icon: Sparkles,
        color: '#ec4899',
        description: 'Cultural change and social transformation programs'
    },
    [ProjectCategory.REMEMBRANCE]: {
        label: 'Doctrine of Remembrance',
        icon: Pin,
        color: '#6b7280',
        description: 'Heritage and remembrance initiatives'
    }
};

// ============================================
// API CONSTANTS
// ============================================

/**
 * API endpoints for project operations
 */
export const API_ENDPOINTS = {
    PROJECTS: '/.netlify/functions/admin-projects',
    PROJECT: (id: string) => `/.netlify/functions/admin-project/${id}`,
    PROJECT_CREATE: '/.netlify/functions/admin-project-create',
    PROJECT_UPDATE: '/.netlify/functions/admin-project-update',
    PROJECT_DELETE: '/.netlify/functions/admin-project-delete',
    PROJECT_BULK_UPDATE: '/.netlify/functions/admin-project-bulk-update',
    PROJECT_BULK_DELETE: '/.netlify/functions/admin-project-bulk-delete'
} as const;

// ============================================
// UI CONSTANTS
// ============================================

/**
 * Animation durations (in milliseconds)
 */
export const ANIMATION_DURATIONS = {
    FADE_IN: 400,
    SLIDE_IN: 300,
    TOAST: 5000,
    MODAL: 300
} as const;

/**
 * Responsive breakpoints (in pixels)
 */
export const BREAKPOINTS = {
    MOBILE: 480,
    TABLET: 768,
    DESKTOP: 1024,
    WIDE: 1280
} as const;

// ============================================
// FORM CONSTANTS
// ============================================

/**
 * Form field limits and validation
 */
export const FORM_LIMITS = {
    TITLE_MIN: 5,
    TITLE_MAX: 100,
    SUBTITLE_MAX: 200,
    DESCRIPTION_MIN: 20,
    DESCRIPTION_MAX: 500,
    FULL_DESCRIPTION_MAX: 5000,
    TAGS_MAX: 10
} as const;

/**
 * File upload limits
 */
export const FILE_LIMITS = {
    MAX_SIZE: 5 * 1024 * 1024, // 5MB
    ACCEPTED_TYPES: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
    MAX_FILES: 1
} as const;

// ============================================
// TOAST CONSTANTS
// ============================================

/**
 * Toast notification configuration
 */
export const TOAST_CONFIG = {
    POSITION: 'top-right' as const,
    DURATION: 5000,
    MAX_TOASTS: 3
} as const;

// ============================================
// LOCAL STORAGE KEYS
// ============================================

/**
 * Local storage keys for project settings
 */
export const STORAGE_KEYS = {
    PROJECT_FILTERS: 'project_filters',
    PROJECT_PAGINATION: 'project_pagination',
    PROJECT_SORT: 'project_sort'
} as const;